import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useFreeTrialFeature } from '../contexts/FeatureFlagContext';
import { useTenantColors } from '../contexts/TenantBrandingContext';
import { useAuth } from '../contexts/AuthContext';
import { GoogleBusinessSetupModal } from '../components/GoogleBusinessSetupModal';
import { HelpIcon } from '../components/HelpIcon';
import { InlineTip } from '../components/InlineTip';
import { TooltipProvider } from '../components/ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import { api } from '../lib/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Icons
const TrendingUpIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendingDownIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const EmailIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const OpenIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const ClickIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
  </svg>
);

const HappyIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FeedbackIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const StarIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease';
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
  clickable?: boolean;
  statusBadge?: React.ReactNode;
  helpKey?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  changeType,
  icon,
  color,
  onClick,
  clickable = false,
  statusBadge,
  helpKey,
}) => {
  const ChangeIcon = changeType === 'increase' ? TrendingUpIcon : TrendingDownIcon;
  const changeColor = changeType === 'increase' ? 'text-green-600' : 'text-red-600';
  const helpContent = helpKey ? getHelpContent(helpKey) : undefined;

  return (
    <div 
      className={`bg-white overflow-hidden shadow rounded-lg ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="p-3 rounded-md" style={{ backgroundColor: `${color}20` }}>
              <div style={{ color }}>{icon}</div>
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate flex items-center gap-1">
                {title}
                {helpContent && (
                  <HelpIcon
                    tooltip={helpContent.content}
                    modalTitle={helpContent.title}
                    modalContent={helpContent.content}
                    size="sm"
                    variant="subtle"
                  />
                )}
              </dt>
              <dd className="flex items-baseline justify-between">
                <div className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">{value}</div>
                  {change !== undefined && (
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${changeColor}`}>
                      <ChangeIcon />
                      <span className="sr-only">
                        {changeType === 'increase' ? 'Increased' : 'Decreased'} by
                      </span>
                      {Math.abs(change)}%
                    </div>
                  )}
                </div>
                {statusBadge}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ChartData {
  date: string;
  value: number;
}

interface DashboardKPIs {
  total_sends: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  happy_rate: number;
  open_feedback_count: number;
  google_places_rating?: number;
  google_places_review_count?: number;
  google_places_name?: string;
  last_updated: string;
}

export const DashboardPage: React.FC = () => {
  const { primary, accent, secondary } = useTenantColors();
  const isFreeTrialEnabled = useFreeTrialFeature();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showGoogleSetup, setShowGoogleSetup] = useState(false);
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);

  // Check if user is new (created within last 7 days) and show onboarding banner
  useEffect(() => {
    if (user?.created_at) {
      const userCreatedDate = new Date(user.created_at);
      const daysSinceCreation = (Date.now() - userCreatedDate.getTime()) / (1000 * 60 * 60 * 24);
      const isNewUser = daysSinceCreation <= 7;
      
      // Check if banner was dismissed
      const bannerDismissed = localStorage.getItem('onboarding_banner_dismissed') === 'true';
      
      if (isNewUser && !bannerDismissed) {
        setShowOnboardingBanner(true);
      }
    }
  }, [user]);

  // Fetch dashboard KPIs
  const { data: kpis, isLoading: kpisLoading, error: kpisError } = useQuery({
    queryKey: ['analytics', 'dashboard', 'kpis'],
    queryFn: async () => {
      const response = await api.analytics.getDashboardKPIs();
      console.log('Dashboard KPIs response:', response.data);
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    onError: (error) => {
      console.error('Error fetching dashboard KPIs:', error);
    },
  });

  const isGoogleLinked = !!(
    kpis?.google_places_name ||
    kpis?.google_places_rating ||
    kpis?.google_places_review_count
  );

  // Fetch verification status if Google Places is connected
  const { data: verificationStatus } = useQuery({
    queryKey: ['verification-status'],
    queryFn: async () => {
      const response = await api.analytics.getVerificationStatus();
      return response.data;
    },
    enabled: isGoogleLinked, // Only fetch if Google Places is connected
    refetchInterval: false,
  });

  // Fetch public settings to check deep link verification bypass
  const { data: publicSettings = [] } = useQuery<Array<{ key: string; value: string }>>({
    queryKey: ['public-settings'],
    queryFn: async () => {
      try {
        const response = await api.serverAdmin.getPublicSettings();
        return response.data || [];
      } catch (error) {
        return [];
      }
    }
  });

  const deepLinkBypassEnabled = publicSettings.find(
    (setting) => setting.key === 'deep_link_verification_bypass'
  )?.value?.toLowerCase() === 'true';

  // Fetch analytics overview for engagement rates
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics', 'overview', '30d', 'daily'],
    queryFn: async () => {
      const response = await api.analytics.getOverview({
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        end_date: new Date().toISOString(),
        period_type: 'daily',
      });
      console.log('Dashboard Overview response:', response.data);
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds to keep charts updated
  });

  // Fetch recent webhook events for dashboard widget
  const { data: recentWebhookEvents } = useQuery({
    queryKey: ['recent-webhook-events'],
    queryFn: async () => {
      try {
        const response = await api.automation.getRecentWebhookEvents(10);
        return response.data || [];
      } catch (error) {
        // Silently fail if automation feature is not available
        return [];
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch campaign analytics for time-series charts
  const { data: campaignAnalytics, isLoading: campaignAnalyticsLoading } = useQuery({
    queryKey: ['analytics', 'campaigns', '30d', 'daily'],
    queryFn: async () => {
      const response = await api.analytics.getCampaignAnalytics({
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        end_date: new Date().toISOString(),
        period_type: 'daily',
      });
      console.log('Campaign Analytics response:', response.data);
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch recent activities
  const { data: recentActivities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['recent-activities'],
    queryFn: async () => {
      const response = await api.analytics.getRecentActivities(10);
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  if (kpisLoading || overviewLoading || campaignAnalyticsLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg animate-pulse">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-200 rounded-md"></div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kpisError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading dashboard data
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>Unable to load dashboard KPIs. Please try refreshing the page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const kpiData: DashboardKPIs = kpis || {
    total_sends: 0,
    delivery_rate: 0,
    open_rate: 0,
    click_rate: 0,
    happy_rate: 0,
    open_feedback_count: 0,
    google_places_name: undefined,
    last_updated: new Date().toISOString(),
  };
  
  // Debug logging
  if (kpis) {
    console.log('Dashboard KPI Data:', kpiData);
  }
  if (overview) {
    console.log('Dashboard Overview Data:', overview);
    console.log('Engagement Rates:', {
      delivery_rate: overview.delivery_rate,
      open_rate: overview.open_rate,
      click_rate: overview.click_rate,
      happy_rate: overview.happy_rate,
    });
  }

  const dashboardHelp = getHelpContent('dashboardOverview');
  const googleBusinessHelp = getHelpContent('googleBusinessProfile');

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              {dashboardHelp && (
                <HelpIcon
                  tooltip="Learn about dashboard metrics"
                  modalTitle={dashboardHelp.title}
                  modalContent={dashboardHelp.content}
                  size="md"
                />
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Overview of your reputation management performance
            </p>
          </div>
        </div>

        {/* Onboarding Banner for New Users */}
        {showOnboardingBanner && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">Complete Your Setup</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Finish setting up your account by customizing your branding, integrations, and preferences.</p>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={() => {
                      navigate('/onboarding');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Complete Setup
                  </button>
                  <button
                    onClick={() => {
                      localStorage.setItem('onboarding_banner_dismissed', 'true');
                      setShowOnboardingBanner(false);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => {
                    localStorage.setItem('onboarding_banner_dismissed', 'true');
                    setShowOnboardingBanner(false);
                  }}
                  className="text-blue-400 hover:text-blue-600"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* First-time user tip */}
        {kpiData.total_sends === 0 && (
          <InlineTip
            type="info"
            title="Get Started"
            content="Create your first campaign to start collecting customer feedback and building your online reputation. Click 'Campaigns' in the sidebar to begin."
            dismissible={true}
            action={{
              label: 'Go to Campaigns',
              onClick: () => window.location.href = '/campaigns',
            }}
          />
        )}

      {/* Trial/Payment Status Banner */}
      {!isFreeTrialEnabled && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Payment Required</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Add payment method to continue using all features and access full analytics.</p>
                <div className="mt-2">
                  <a
                    href="/billing"
                    className="font-medium text-red-800 hover:text-red-900 underline"
                  >
                    Go to Billing →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Business Setup Card */}
      {!kpiData.google_places_rating && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <StarIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-blue-900">
                  Connect Your Google Business Profile
                </h3>
                {googleBusinessHelp && (
                  <HelpIcon
                    tooltip="Learn about Google Business integration"
                    modalTitle={googleBusinessHelp.title}
                    modalContent={googleBusinessHelp.content}
                    size="sm"
                    variant="subtle"
                  />
                )}
              </div>
              <p className="text-sm text-blue-700 mt-1">
                Link your Google Business listing to display your rating and reviews on the dashboard.
              </p>
            </div>
            <div className="ml-4">
              <button
                onClick={() => setShowGoogleSetup(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Connect Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Business Update Card - shown when already connected */}
      {kpiData.google_places_rating && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-green-900">
                Google Business Profile Connected
                {kpiData.google_places_name && (
                  <span className="font-semibold"> - {kpiData.google_places_name}</span>
                )}
              </h3>
              <p className="text-xs text-green-700 mt-1">
                Click the Google Rating card below to update or change your linked business.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Sends"
          value={kpiData.total_sends.toLocaleString()}
          icon={<EmailIcon />}
          color={primary}
          helpKey="dashboardKPITotalSends"
        />
        
        <KPICard
          title="Open Rate"
          value={`${kpiData.open_rate.toFixed(1)}%`}
          icon={<OpenIcon />}
          color={accent}
          helpKey="dashboardKPIOpenRate"
        />
        
        <KPICard
          title="Click Rate"
          value={`${kpiData.click_rate.toFixed(1)}%`}
          icon={<ClickIcon />}
          color={secondary}
          helpKey="dashboardKPIClickRate"
        />
        
        <KPICard
          title="Happy Rate"
          value={`${kpiData.happy_rate.toFixed(1)}%`}
          icon={<HappyIcon />}
          color={primary}
          helpKey="dashboardKPIHappyRate"
        />
      </div>

      {/* Additional metrics row */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <KPICard
          title="Open Feedback"
          value={kpiData.open_feedback_count}
          icon={<FeedbackIcon />}
          color={accent}
        />
        
        <KPICard
          title="Google Rating"
          value={kpiData.google_places_rating ? `${kpiData.google_places_rating.toFixed(1)} ⭐` : "Not Connected"}
          icon={<StarIcon />}
          color={primary}
          onClick={() => setShowGoogleSetup(true)}
          clickable={true}
          statusBadge={!isGoogleLinked ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Setup Required
            </span>
          ) : verificationStatus?.status === 'verified' || deepLinkBypassEnabled ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Verified
            </span>
          ) : verificationStatus?.status === 'pending' ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Pending
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Connected
            </span>
          )}
        />
        
        {kpiData.google_places_review_count && (
          <KPICard
            title="Google Reviews"
            value={kpiData.google_places_review_count.toLocaleString()}
            icon={<StarIcon />}
            color={secondary}
          />
        )}
      </div>

      {/* Charts section */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Performance chart */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Performance Overview
            </h3>
            {campaignAnalytics && Array.isArray(campaignAnalytics) && campaignAnalytics.length > 0 ? (
              <div className="h-64">
                <Line
                  data={{
                    labels: campaignAnalytics.map((item: any) => {
                      if (!item || !item.date) return 'Invalid Date';
                      try {
                        const date = new Date(item.date);
                        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      } catch (e) {
                        return 'Invalid Date';
                      }
                    }),
                    datasets: [
                      {
                        label: 'Sends',
                        data: campaignAnalytics.map((item: any) => item?.sent_count ?? item?.total_sends ?? 0),
                        borderColor: primary,
                        backgroundColor: `${primary}20`,
                        tension: 0.4,
                        fill: true,
                      },
                      {
                        label: 'Opens',
                        data: campaignAnalytics.map((item: any) => item?.opened_count ?? 0),
                        borderColor: accent,
                        backgroundColor: `${accent}20`,
                        tension: 0.4,
                        fill: false,
                      },
                      {
                        label: 'Clicks',
                        data: campaignAnalytics.map((item: any) => item?.clicked_count ?? 0),
                        borderColor: secondary,
                        backgroundColor: `${secondary}20`,
                        tension: 0.4,
                        fill: false,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0,
                        },
                      },
                    },
                    interaction: {
                      intersect: false,
                      mode: 'index' as const,
                    },
                  }}
                />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="mt-2">No data available yet</p>
                  <p className="text-sm text-gray-400">Create campaigns to see performance data</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Engagement rates chart */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Engagement Rates
            </h3>
            {(kpiData || overview) ? (() => {
              // Use KPI data first (it has correct real-time values), fallback to overview
              const deliveryRate = Number(kpiData?.delivery_rate || overview?.delivery_rate) || 0;
              const openRate = Number(kpiData?.open_rate || overview?.open_rate) || 0;
              const clickRate = Number(kpiData?.click_rate || overview?.click_rate) || 0;
              const happyRate = Number(kpiData?.happy_rate || overview?.happy_rate) || 0;
              
              console.log('Engagement Rates Chart Data:', {
                deliveryRate,
                openRate,
                clickRate,
                happyRate,
                usingKPIData: !!kpiData,
                kpiData,
                overview
              });
              
              const chartData = {
                labels: ['Delivery Rate', 'Open Rate', 'Click Rate', 'Happy Rate'],
                datasets: [
                  {
                    label: 'Rate (%)',
                    data: [deliveryRate, openRate, clickRate, happyRate],
                    backgroundColor: [
                      'rgba(59, 130, 246, 0.8)',
                      'rgba(16, 185, 129, 0.8)',
                      'rgba(245, 158, 11, 0.8)',
                      'rgba(236, 72, 153, 0.8)',
                    ],
                    borderColor: [
                      'rgb(59, 130, 246)',
                      'rgb(16, 185, 129)',
                      'rgb(245, 158, 11)',
                      'rgb(236, 72, 153)',
                    ],
                    borderWidth: 2,
                  },
                ],
              };
              
              return (
                <div className="h-64">
                  <Bar
                    data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top' as const,
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          callback: function(value) {
                            return value + '%';
                          },
                        },
                      },
                      x: {
                        ticks: {
                          autoSkip: false,
                        },
                      },
                    },
                  }}
                  />
                </div>
              );
            })() : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="mt-2">No engagement data yet</p>
                  <p className="text-sm text-gray-400">Send campaigns to see engagement rates</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Webhook Activity Widget */}
      {recentWebhookEvents && recentWebhookEvents.length > 0 && (
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Webhook Activity
              </h3>
              <a
                href="/settings/automations"
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                View All →
              </a>
            </div>
            <div className="space-y-3">
              {recentWebhookEvents.slice(0, 5).map((event: any) => {
                const formatTimeAgo = (dateString: string) => {
                  const date = new Date(dateString);
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  
                  if (diffMins < 1) return 'Just now';
                  if (diffMins < 60) return `${diffMins}m ago`;
                  if (diffHours < 24) return `${diffHours}h ago`;
                  return date.toLocaleDateString();
                };
                
                return (
                  <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-3 flex-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        event.status === 'success' ? 'bg-green-100 text-green-800' :
                        event.status === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {event.status}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {event.event_type === 'contact_created' ? 'Contact Created' :
                           event.event_type === 'contact_updated' ? 'Contact Updated' :
                           event.event_type === 'campaign_queued' ? 'Campaign Queued' :
                           event.event_type}
                        </p>
                        {event.error_message && (
                          <p className="text-xs text-red-600 mt-1 truncate">{event.error_message}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 ml-3">
                      {formatTimeAgo(event.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Recent activity section */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          {activitiesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center text-sm animate-pulse">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivities && recentActivities.length > 0 ? (
            <div className="space-y-3">
              {recentActivities.map((activity: any) => {
                const getActivityColor = (type: string) => {
                  if (type.includes('created') || type.includes('received')) return 'bg-green-400';
                  if (type.includes('resolved') || type.includes('completed')) return 'bg-blue-400';
                  if (type.includes('low_rating') || type.includes('opted_out')) return 'bg-red-400';
                  if (type.includes('updated') || type.includes('linked')) return 'bg-yellow-400';
                  return 'bg-gray-400';
                };
                
                const formatTimeAgo = (dateString: string) => {
                  const date = new Date(dateString);
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);
                  
                  if (diffMins < 1) return 'Just now';
                  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
                  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                  return date.toLocaleDateString();
                };
                
                return (
                  <div key={activity.id} className="flex items-center text-sm">
                    <div className="flex-shrink-0">
                      <div className={`w-2 h-2 ${getActivityColor(activity.activity_type)} rounded-full`}></div>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-gray-900">{activity.title}</p>
                      {activity.description && (
                        <p className="text-gray-500 text-xs mt-0.5">{activity.description}</p>
                      )}
                      <p className="text-gray-400 text-xs mt-1">{formatTimeAgo(activity.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm">No recent activity</p>
              <p className="text-gray-400 text-xs mt-1">Activity will appear here as you use the application</p>
            </div>
          )}
        </div>
      </div>

      {/* Last updated */}
      <div className="text-xs text-gray-500 text-center">
        Last updated: {new Date(kpiData.last_updated).toLocaleString()}
      </div>

      {/* Google Business Setup Modal */}
      <GoogleBusinessSetupModal
        isOpen={showGoogleSetup}
        onClose={() => setShowGoogleSetup(false)}
        isRelinking={!!kpiData.google_places_rating}
      />
      </div>
    </TooltipProvider>
  );
};
